import { PDFDocument, rgb, StandardFonts } from 'pdf-lib'

import { savePdf } from '../../../../helpers/pdf'
import { convertToDate } from '../../../../helpers/time'
import { calculateJudgeScores, getJudgeName, wrapText } from './utils'

import { IEventReviewPublish } from '../../../../models/event-review-publish/event-review-publish.interface'
import { IRegistrationByDayInterface } from '../../../../models/registrations-by-day/registrationByDay.interface'
import { IUserInterface } from '../../../../models/users/user.interface'

export const createScoringResultsPdf = async (
  event: IEventReviewPublish,
  rider: IRegistrationByDayInterface,
  judges: IUserInterface[]
) => {
  // Create a new PDF document in portrait orientation
  const pdfDoc = await PDFDocument.create()

  // Load fonts
  const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica)
  const helveticaBoldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold)

  const pageWidth = 612 // 8.5 inches * 72 points
  const pageHeight = 792 // 11 inches * 72 points
  const margin = 36 // 0.5 inch margins
  const contentWidth = pageWidth - 2 * margin

  const riderName = rider.riderName || 'Unknown Rider'
  const scoreCard = rider.score?.sheet?.name || 'Unknown Score Card'

  // Calculate overall totals
  let overallTotal = 0
  let overallMax = 0
  let overallPercentage = '0.000'

  if (rider.score?.judges && rider.score.judges.length > 0) {
    rider.score.judges.forEach((judge) => {
      if (judge.sections) {
        judge.sections.forEach((section) => {
          section.rows.forEach((row) => {
            if (row.coefficient) {
              overallTotal += Number(row.coefficient) * Number(row.score || 0)
            } else if (row.fraction === 'minus') {
              overallTotal -= Number(row.score || 0)
            } else {
              overallTotal += Number(row.score || 0)
            }
          })
        })
        overallTotal -= judge.errors || 0
      }
    })
    overallTotal = Math.round((overallTotal / rider.score.judges.length) * 1000) / 1000
    if (rider.score.judges[0]?.sections) {
      rider.score.judges[0].sections.forEach((section) => {
        overallMax += section.max || 0
      })
    }
    overallPercentage = overallMax > 0 ? ((overallTotal / overallMax) * 100).toFixed(3) : '0.000'
  }

  // ==================== RESULTS SUMMARY PAGE ====================
  const summaryPage = pdfDoc.addPage([pageWidth, pageHeight])

  // Header section
  const headerHeight = 170

  // Header content positioning
  let headerY = pageHeight - 105

  // Event name (main prominent title)
  summaryPage.drawText(event.EventDetails?.competitionName || 'Event Name', {
    x: margin,
    y: headerY,
    size: 22,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0), // Black text
  })

  // Event details (smaller subtitle)
  headerY -= 15
  const eventStartDate = convertToDate(event.EventDetails?.competitionStartDate) || new Date()

  // Format date as DD/MM/YYYY
  const day = String(eventStartDate.getDate()).padStart(2, '0')
  const month = String(eventStartDate.getMonth() + 1).padStart(2, '0')
  const year = eventStartDate.getFullYear()
  const formattedDate = `${day}/${month}/${year}`

  const eventDetails = `${event.EventDetails?.competitionCity || 'Unknown City'}, ${event.EventDetails?.competitionState || 'Unknown State'} • ${formattedDate}`
  summaryPage.drawText(eventDetails, {
    x: margin,
    y: headerY,
    size: 12,
    font: helveticaFont,
    color: rgb(0.4, 0.4, 0.4), // Gray text for subtitle
  })

  // Pegasus logo on the right side of header
  const logoSize = 40
  const logoX = pageWidth - margin - logoSize
  const logoY = pageHeight - headerHeight + 45

  // Load and embed Pegasus logo
  const logoUrl = '/assets/pegasus-logo.png'
  const logoBytes = await fetch(logoUrl).then((res) => res.arrayBuffer())
  const logoImage = await pdfDoc.embedPng(logoBytes)

  // Draw the actual Pegasus logo
  summaryPage.drawImage(logoImage, {
    x: logoX,
    y: logoY,
    width: logoSize,
    height: logoSize,
    opacity: 1,
  })

  // Add black dividing line underneath header
  const dividerY = logoY - 10
  summaryPage.drawLine({
    start: { x: margin, y: dividerY },
    end: { x: pageWidth - margin, y: dividerY },
    thickness: 1,
    color: rgb(0.8, 0.8, 0.8), // Gray divider line
  })

  // Content section starts below header
  let currentY = pageHeight - headerHeight - 60

  // Score card name section header
  summaryPage.drawText(scoreCard, {
    x: margin,
    y: currentY,
    size: 18,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  })

  currentY -= 50

  // Results table
  const tableStartY = currentY
  const rowHeight = 30
  const colWidths = [80, 300, 180] // Judge, Name, Score columns
  let tableY = tableStartY

  // Table headers
  summaryPage.drawRectangle({
    x: margin,
    y: tableY - rowHeight,
    width: contentWidth,
    height: rowHeight,
    color: rgb(1, 1, 1),
    borderColor: rgb(0.92, 0.92, 0.92),
    borderWidth: 1,
  })

  const headers = ['Judge', 'Name', 'Score']
  let colX = margin
  headers.forEach((header, index) => {
    summaryPage.drawText(header, {
      x: colX + 10,
      y: tableY - 20,
      size: 12,
      font: helveticaBoldFont,
      color: rgb(0, 0, 0),
    })

    if (index < headers.length - 1) {
      summaryPage.drawLine({
        start: { x: colX + colWidths[index], y: tableY },
        end: { x: colX + colWidths[index], y: tableY - rowHeight },
        thickness: 1,
        color: rgb(0.85, 0.85, 0.85),
      })
    }

    colX += colWidths[index]
  })

  tableY -= rowHeight

  // Judge rows
  rider.score?.judges?.forEach((judge, index) => {
    const { judgeTotal, judgePercentage } = calculateJudgeScores(judge)
    const judgeName = getJudgeName(judge, judges, index)

    // Draw row
    summaryPage.drawRectangle({
      x: margin,
      y: tableY - rowHeight,
      width: contentWidth,
      height: rowHeight,
      borderColor: rgb(0.92, 0.92, 0.92),
      borderWidth: 1,
      color: rgb(1, 1, 1),
    })

    const rowData = [`${index + 1}`, judgeName, `${judgeTotal} • ${judgePercentage}%`]
    colX = margin
    rowData.forEach((data, colIndex) => {
      summaryPage.drawText(data, {
        x: colX + 10,
        y: tableY - 20,
        size: 11,
        font: helveticaFont,
        color: rgb(0, 0, 0),
      })

      if (colIndex < rowData.length - 1) {
        summaryPage.drawLine({
          start: { x: colX + colWidths[colIndex], y: tableY },
          end: { x: colX + colWidths[colIndex], y: tableY - rowHeight },
          thickness: 1,
          color: rgb(0.92, 0.92, 0.92),
        })
      }

      colX += colWidths[colIndex]
    })

    tableY -= rowHeight
  })

  // Total row
  summaryPage.drawRectangle({
    x: margin,
    y: tableY - rowHeight,
    width: contentWidth,
    height: rowHeight,
    borderColor: rgb(0.92, 0.92, 0.92),
    borderWidth: 1,
    color: rgb(1, 1, 1),
  })

  const totalRowData = [
    'Total',
    `${rider.score?.judges?.length || 0} Judge${rider.score?.judges?.length !== 1 ? 's' : ''}`,
    `${overallTotal} • ${overallPercentage}%`,
  ]
  colX = margin
  totalRowData.forEach((data, colIndex) => {
    summaryPage.drawText(data, {
      x: colX + 10,
      y: tableY - 20,
      size: 11,
      font: helveticaBoldFont,
      color: rgb(0, 0, 0),
    })

    if (colIndex < totalRowData.length - 1) {
      summaryPage.drawLine({
        start: { x: colX + colWidths[colIndex], y: tableY },
        end: { x: colX + colWidths[colIndex], y: tableY - rowHeight },
        thickness: 1,
        color: rgb(0.92, 0.92, 0.92),
      })
    }

    colX += colWidths[colIndex]
  })

  tableY -= rowHeight

  // Status row
  summaryPage.drawRectangle({
    x: margin,
    y: tableY - rowHeight,
    width: contentWidth,
    height: rowHeight,
    borderColor: rgb(0.92, 0.92, 0.92),
    borderWidth: 1,
    color: rgb(1, 1, 1),
  })

  const statusRowData = ['S', 'Status', rider.score?.scoreStatus || 'N/A']
  colX = margin
  statusRowData.forEach((data, colIndex) => {
    summaryPage.drawText(data, {
      x: colX + 10,
      y: tableY - 20,
      size: 11,
      font: helveticaBoldFont,
      color: rgb(0, 0, 0),
    })

    if (colIndex < statusRowData.length - 1) {
      summaryPage.drawLine({
        start: { x: colX + colWidths[colIndex], y: tableY },
        end: { x: colX + colWidths[colIndex], y: tableY - rowHeight },
        thickness: 1,
        color: rgb(0.92, 0.92, 0.92),
      })
    }

    colX += colWidths[colIndex]
  })

  // ==================== INDIVIDUAL JUDGE PAGES ====================
  if (rider.score?.judges) {
    for (const [judgeIndex, judge] of rider.score.judges.entries()) {
      const judgePage = pdfDoc.addPage([pageWidth, pageHeight])
      let judgePageY = pageHeight - margin

      const judgeName = getJudgeName(judge, judges, judgeIndex)
      const { judgeMax, judgePercentage } = calculateJudgeScores(judge)

      // Judge header
      judgePage.drawText(`Judge ${judgeIndex + 1}, ${judgeName}`, {
        x: margin,
        y: judgePageY,
        size: 16,
        font: helveticaBoldFont,
        color: rgb(0, 0, 0),
      })

      judgePageY -= 40

      // Process sections separately - group by section name/type for dynamic handling
      // Track current page and Y position consistently
      let currentPage = judgePage
      let currentY = judgePageY

      if (judge.sections) {
        let movementNumber = 1
        let shouldBreak = false

        // Group sections by their name for dynamic processing
        const sectionGroups: { [key: string]: any[] } = {}
        judge.sections.forEach((section) => {
          const sectionName = section.name || 'Unknown Section'
          if (!sectionGroups[sectionName]) {
            sectionGroups[sectionName] = []
          }
          sectionGroups[sectionName].push(section)
        })

        // Process each section group
        for (const [sectionName, sections] of Object.entries(sectionGroups)) {
          if (shouldBreak) break

          // Calculate section max
          const sectionMax = sections.reduce((total, section) => total + (section.max || 0), 0)

          // Determine if this section uses movement numbers
          // Sections with names containing "technical", "execution", "individual", or "tasks" use movements
          // Sections with names containing "artistic", "collective", "marks", "impression" don't use movements
          const usesMovements =
            sectionName.toLowerCase().includes('technical') ||
            sectionName.toLowerCase().includes('execution') ||
            sectionName.toLowerCase().includes('individual') ||
            sectionName.toLowerCase().includes('tasks')

          // Check if we need space for section header
          if (currentY < 100) {
            currentPage = pdfDoc.addPage([pageWidth, pageHeight])
            currentY = pageHeight - margin

            // Add judge header on new page
            currentPage.drawText(`Judge ${judgeIndex + 1}, ${judgeName} (continued)`, {
              x: margin,
              y: currentY,
              size: 16,
              font: helveticaBoldFont,
              color: rgb(0, 0, 0),
            })
            currentY -= 40
          }

          // Section header
          currentPage.drawText(`${sectionName.toUpperCase()} • Max ${sectionMax}`, {
            x: margin,
            y: currentY,
            size: 12,
            font: helveticaBoldFont,
            color: rgb(0, 0, 0),
          })

          currentY -= 30

          if (usesMovements) {
            // Movement-based sections (like Technical Execution)
            const taskColWidths = [60, 280, 60, 60, 80] // Mvmt, Test, Coeffic., Score, Final
            const taskHeaders = ['Mvmt', 'Test', 'Coeffic.', 'Score', 'Final']

            // Task headers with dark background
            currentPage.drawRectangle({
              x: margin,
              y: currentY - 25,
              width: contentWidth,
              height: 25,
              color: rgb(0.2, 0.2, 0.2),
              borderColor: rgb(0.92, 0.92, 0.92),
              borderWidth: 1,
            })

            let taskColX = margin
            taskHeaders.forEach((header, index) => {
              currentPage.drawText(header, {
                x: taskColX + 10,
                y: currentY - 18,
                size: 10,
                font: helveticaBoldFont,
                color: rgb(1, 1, 1),
              })

              if (index < taskHeaders.length - 1) {
                currentPage.drawLine({
                  start: { x: taskColX + taskColWidths[index], y: currentY },
                  end: { x: taskColX + taskColWidths[index], y: currentY - 25 },
                  thickness: 1,
                  color: rgb(0.92, 0.92, 0.92),
                })
              }

              taskColX += taskColWidths[index]
            })

            currentY -= 25

            // Process movement-based rows
            for (const section of sections) {
              for (const row of section.rows) {
                // Skip deduction/error rows - we'll handle them separately
                const description = (row.description || '').toLowerCase()
                const isDeductionRow =
                  description.includes('deduction') ||
                  description.includes('error') ||
                  description.includes('forbidden')

                if (isDeductionRow) {
                  continue // Skip for now, process later
                }

                // Check if we need a new page
                if (currentY < 150) {
                  currentPage = pdfDoc.addPage([pageWidth, pageHeight])
                  currentY = pageHeight - margin

                  // Add judge header on new page
                  currentPage.drawText(`Judge ${judgeIndex + 1}, ${judgeName} (continued)`, {
                    x: margin,
                    y: currentY,
                    size: 16,
                    font: helveticaBoldFont,
                    color: rgb(0, 0, 0),
                  })
                  currentY -= 40

                  // Add section header on new page
                  currentPage.drawText(
                    `${sectionName.toUpperCase()} • Max ${sectionMax} (continued)`,
                    {
                      x: margin,
                      y: currentY,
                      size: 12,
                      font: helveticaBoldFont,
                      color: rgb(0, 0, 0),
                    }
                  )
                  currentY -= 30

                  // Re-draw headers on new page
                  currentPage.drawRectangle({
                    x: margin,
                    y: currentY - 25,
                    width: contentWidth,
                    height: 25,
                    color: rgb(0.2, 0.2, 0.2),
                    borderColor: rgb(0.92, 0.92, 0.92),
                    borderWidth: 1,
                  })

                  let newPageColX = margin
                  taskHeaders.forEach((header, index) => {
                    currentPage.drawText(header, {
                      x: newPageColX + 10,
                      y: currentY - 18,
                      size: 10,
                      font: helveticaBoldFont,
                      color: rgb(1, 1, 1),
                    })

                    if (index < taskHeaders.length - 1) {
                      currentPage.drawLine({
                        start: { x: newPageColX + taskColWidths[index], y: currentY },
                        end: { x: newPageColX + taskColWidths[index], y: currentY - 25 },
                        thickness: 1,
                        color: rgb(0.92, 0.92, 0.92),
                      })
                    }

                    newPageColX += taskColWidths[index]
                  })

                  currentY -= 25
                }

                // Parse test description for movements (existing logic)
                const parseTestDescription = (description: string): string[] => {
                  if (!description) return [`Movement ${movementNumber}`]

                  let cleanedDescription = description
                    .replace(/<\/span\s+([^>]*)/g, '</span> $1')
                    .replace(/\s+/g, ' ')

                  const pTagMatches = cleanedDescription.match(/<p[^>]*>(.*?)<\/p>/g)

                  if (pTagMatches) {
                    const allLines: string[] = []

                    pTagMatches.forEach((pTag) => {
                      let content = pTag.replace(/<\/?p[^>]*>/g, '')
                      const parts = content.split(/(<span[^>]*>.*?<\/span>)/)

                      let currentLine = ''

                      parts.forEach((part) => {
                        if (part.match(/<span[^>]*>/)) {
                          if (currentLine.trim()) {
                            allLines.push(currentLine.trim())
                          }
                          currentLine = part.replace(/<\/?span[^>]*>/g, '').trim()
                        } else {
                          const textContent = part.replace(/<[^>]*>/g, '').trim()
                          if (textContent) {
                            currentLine += (currentLine ? ' ' : '') + textContent
                          }
                        }
                      })

                      if (currentLine.trim()) {
                        allLines.push(currentLine.trim())
                      }
                    })

                    const filteredLines = allLines.filter((line) => {
                      return (
                        line &&
                        line.length > 1 &&
                        !line.match(/^•\s*$/) &&
                        !line.match(/^N\/A\s*•?\s*$/) &&
                        line !== 'N/A'
                      )
                    })

                    if (filteredLines.length > 0) {
                      return filteredLines
                    }
                  }

                  const cleanText = description
                    .replace(/<[^>]*>/g, '')
                    .replace(/&nbsp;/g, ' ')
                    .replace(/&amp;/g, '&')
                    .replace(/\s+/g, ' ')
                    .trim()

                  if (cleanText.includes('•')) {
                    const lines = cleanText
                      .split(/(?=[A-Z]\s*•)|(?=X\s*•)|(?=N\/A\s*•)|(?=Between\s+[A-Z])/)
                      .map((part) => part.trim())
                      .filter((part) => part && part.length > 2)

                    if (lines.length > 0) {
                      return lines
                    }
                  }

                  return cleanText ? [cleanText] : [`Movement ${movementNumber}`]
                }

                // Helper function to parse and render text with bold movement letters
                const renderTextWithBoldLetters = (
                  page: any,
                  text: string,
                  x: number,
                  y: number,
                  fontSize: number,
                  regularFont: any,
                  boldFont: any,
                  color: any
                ) => {
                  // Split on bullet to separate movement indicator from description
                  const bulletIndex = text.indexOf('•')
                  let currentX = x

                  if (bulletIndex !== -1) {
                    // Text before bullet (including bullet) should be bold
                    const beforeBullet = text.substring(0, bulletIndex + 1).trim()
                    // Text after bullet should be regular
                    const afterBullet = text.substring(bulletIndex + 1).trim()

                    if (beforeBullet) {
                      page.drawText(beforeBullet, {
                        x: currentX,
                        y: y,
                        size: fontSize,
                        font: boldFont,
                        color: color,
                      })
                      currentX += boldFont.widthOfTextAtSize(beforeBullet, fontSize)
                    }

                    if (afterBullet) {
                      // Add a space between bold and regular text
                      currentX += boldFont.widthOfTextAtSize(' ', fontSize)
                      page.drawText(afterBullet, {
                        x: currentX,
                        y: y,
                        size: fontSize,
                        font: regularFont,
                        color: color,
                      })
                    }
                  } else {
                    // No bullet found, render entire text as regular
                    page.drawText(text, {
                      x: currentX,
                      y: y,
                      size: fontSize,
                      font: regularFont,
                      color: color,
                    })
                  }
                }

                // Continue with existing movement processing logic...
                const parsedLines = parseTestDescription(row.description || '')
                const testLines: string[] = []
                const testColumnWidth = taskColWidths[1] - 10

                parsedLines.forEach((line) => {
                  const wrappedLines = wrapText(line, testColumnWidth, 9, helveticaFont)
                  testLines.push(...wrappedLines)
                })

                const rowHeight = Math.max(25, testLines.length * 12 + 10)

                // Draw movement row (existing logic)
                currentPage.drawRectangle({
                  x: margin,
                  y: currentY - rowHeight,
                  width: contentWidth,
                  height: rowHeight,
                  borderColor: rgb(0.92, 0.92, 0.92),
                  borderWidth: 1,
                  color: rgb(1, 1, 1),
                })

                const coefficient = row.coefficient || ''
                const score = row.score || ''
                const finalScore =
                  row.coefficient && row.score
                    ? (Number(row.coefficient) * Number(row.score)).toString()
                    : score

                const taskData = [
                  `${movementNumber}`,
                  testLines,
                  `${coefficient}`,
                  `${score}`,
                  `${finalScore}`,
                ]

                taskColX = margin
                for (let colIndex = 0; colIndex < taskData.length; colIndex++) {
                  const data = taskData[colIndex]

                  if (colIndex === 1 && Array.isArray(data)) {
                    data.forEach((line, lineIndex) => {
                      renderTextWithBoldLetters(
                        currentPage,
                        line,
                        taskColX + 5,
                        currentY - 12 - lineIndex * 12,
                        9,
                        helveticaFont,
                        helveticaBoldFont,
                        rgb(0, 0, 0)
                      )
                    })
                  } else {
                    let displayText = String(data)
                    let useFont = helveticaFont

                    if (colIndex === 0 || colIndex === 4) {
                      useFont = helveticaBoldFont
                    }

                    let textX = taskColX + 5
                    if (colIndex >= 2) {
                      const columnWidth = taskColWidths[colIndex]
                      const textWidth = useFont.widthOfTextAtSize(displayText, 9)
                      textX = taskColX + (columnWidth - textWidth) / 2
                    }

                    const textY = currentY - rowHeight / 2 - 2

                    currentPage.drawText(displayText, {
                      x: textX,
                      y: textY,
                      size: 9,
                      font: useFont,
                      color: rgb(0, 0, 0),
                    })
                  }

                  if (colIndex < taskData.length - 1) {
                    currentPage.drawLine({
                      start: { x: taskColX + taskColWidths[colIndex], y: currentY },
                      end: { x: taskColX + taskColWidths[colIndex], y: currentY - rowHeight },
                      thickness: 1,
                      color: rgb(0.92, 0.92, 0.92),
                    })
                  }

                  taskColX += taskColWidths[colIndex]
                }

                currentY -= rowHeight

                // Handle remarks for movement rows
                const rowRemarks = row.remarks || row.remark || row.comment || ''
                if (rowRemarks && rowRemarks.trim() && currentY > 100) {
                  const remarksLabelWidth = helveticaBoldFont.widthOfTextAtSize('Remarks:', 8)
                  const spaceWidth = helveticaBoldFont.widthOfTextAtSize(' ', 8)
                  const remarksTextWidth = taskColWidths[1] - remarksLabelWidth - spaceWidth - 10
                  const remarksLines = wrapText(rowRemarks, remarksTextWidth, 8, helveticaFont)
                  const remarksHeight = Math.max(20, remarksLines.length * 12 + 8)

                  currentPage.drawRectangle({
                    x: margin,
                    y: currentY - remarksHeight,
                    width: contentWidth,
                    height: remarksHeight,
                    borderColor: rgb(0.92, 0.92, 0.92),
                    borderWidth: 1,
                    color: rgb(1, 1, 1),
                  })

                  let remarksColX = margin
                  for (let colIndex = 0; colIndex < taskColWidths.length - 1; colIndex++) {
                    remarksColX += taskColWidths[colIndex]
                    currentPage.drawLine({
                      start: { x: remarksColX, y: currentY },
                      end: { x: remarksColX, y: currentY - remarksHeight },
                      thickness: 1,
                      color: rgb(0.92, 0.92, 0.92),
                    })
                  }

                  const testColumnX = margin + taskColWidths[0]

                  currentPage.drawText('Remarks:', {
                    x: testColumnX + 5,
                    y: currentY - 12,
                    size: 8,
                    font: helveticaBoldFont,
                    color: rgb(0.3, 0.3, 0.3),
                  })

                  remarksLines.forEach((line, lineIndex) => {
                    currentPage.drawText(line, {
                      x: testColumnX + 5 + remarksLabelWidth + spaceWidth,
                      y: currentY - 12 - lineIndex * 12,
                      size: 8,
                      font: helveticaFont,
                      color: rgb(0.4, 0.4, 0.4),
                    })
                  })

                  currentY -= remarksHeight
                }

                movementNumber++
              }
            }

            // Now process deduction/error rows in a separate section
            const deductionRows: any[] = []
            for (const section of sections) {
              for (const row of section.rows) {
                const description = (row.description || '').toLowerCase()
                const isDeductionRow =
                  description.includes('deduction') ||
                  description.includes('error') ||
                  description.includes('forbidden')
                if (isDeductionRow) {
                  deductionRows.push(row)
                }
              }
            }

            if (deductionRows.length > 0) {
              // Add space before deductions section
              currentY -= 30

              // Check if we need a new page for deductions
              if (currentY < 200) {
                currentPage = pdfDoc.addPage([pageWidth, pageHeight])
                currentY = pageHeight - margin

                // Add judge header on new page
                currentPage.drawText(`Judge ${judgeIndex + 1}, ${judgeName} (continued)`, {
                  x: margin,
                  y: currentY,
                  size: 16,
                  font: helveticaBoldFont,
                  color: rgb(0, 0, 0),
                })
                currentY -= 40
              }

              // Deductions section header
              currentPage.drawText('DEDUCTIONS & ERRORS', {
                x: margin,
                y: currentY,
                size: 12,
                font: helveticaBoldFont,
                color: rgb(0, 0, 0),
              })

              currentY -= 30

              // Simple two-column layout for deductions: Description | Score
              const deductionColWidths = [460, 80] // Description, Score
              const deductionHeaders = ['Description', 'Score']

              // Deduction headers
              currentPage.drawRectangle({
                x: margin,
                y: currentY - 25,
                width: deductionColWidths.reduce((sum, width) => sum + width, 0),
                height: 25,
                color: rgb(0.2, 0.2, 0.2),
                borderColor: rgb(0.92, 0.92, 0.92),
                borderWidth: 1,
              })

              let deductionColX = margin
              deductionHeaders.forEach((header, index) => {
                currentPage.drawText(header, {
                  x: deductionColX + 10,
                  y: currentY - 18,
                  size: 10,
                  font: helveticaBoldFont,
                  color: rgb(1, 1, 1),
                })

                if (index < deductionHeaders.length - 1) {
                  currentPage.drawLine({
                    start: { x: deductionColX + deductionColWidths[index], y: currentY },
                    end: { x: deductionColX + deductionColWidths[index], y: currentY - 25 },
                    thickness: 1,
                    color: rgb(0.92, 0.92, 0.92),
                  })
                }

                deductionColX += deductionColWidths[index]
              })

              currentY -= 25

              // Process each deduction row
              for (const row of deductionRows) {
                // Parse deduction description
                const parseDeductionDescription = (
                  description: string
                ): { title: string; detail: string } => {
                  if (!description) return { title: '', detail: '' }

                  // Clean HTML
                  const cleanText = description
                    .replace(/<[^>]*>/g, '')
                    .replace(/&nbsp;/g, ' ')
                    .replace(/&amp;/g, '&')
                    .trim()

                  // Look for h5 tags (title) and p tags (detail)
                  const h5Match = description.match(/<h5[^>]*>(.*?)<\/h5>/i)
                  const pMatch = description.match(/<p[^>]*>(.*?)<\/p>/i)

                  if (h5Match && pMatch) {
                    return {
                      title: h5Match[1].replace(/<[^>]*>/g, '').trim(),
                      detail: pMatch[1].replace(/<[^>]*>/g, '').trim(),
                    }
                  }

                  // Fallback to clean text
                  return { title: cleanText, detail: '' }
                }

                const { title, detail } = parseDeductionDescription(row.description || '')
                const score = row.score || '0'

                // Calculate row height based on content
                const detailLines = detail
                  ? wrapText(detail, deductionColWidths[0] - 20, 8, helveticaFont)
                  : []
                const rowHeight = Math.max(30, 18 + detailLines.length * 12)

                // Check if we need a new page
                if (currentY < rowHeight + 50) {
                  currentPage = pdfDoc.addPage([pageWidth, pageHeight])
                  currentY = pageHeight - margin

                  // Add continuation headers
                  currentPage.drawText(
                    `Judge ${judgeIndex + 1}, ${judgeName} - Deductions & Errors (continued)`,
                    {
                      x: margin,
                      y: currentY,
                      size: 16,
                      font: helveticaBoldFont,
                      color: rgb(0, 0, 0),
                    }
                  )
                  currentY -= 40

                  // Re-draw headers
                  currentPage.drawRectangle({
                    x: margin,
                    y: currentY - 25,
                    width: deductionColWidths.reduce((sum, width) => sum + width, 0),
                    height: 25,
                    color: rgb(0.2, 0.2, 0.2),
                    borderColor: rgb(0.92, 0.92, 0.92),
                    borderWidth: 1,
                  })

                  deductionColX = margin
                  deductionHeaders.forEach((header, index) => {
                    currentPage.drawText(header, {
                      x: deductionColX + 10,
                      y: currentY - 18,
                      size: 10,
                      font: helveticaBoldFont,
                      color: rgb(1, 1, 1),
                    })

                    if (index < deductionHeaders.length - 1) {
                      currentPage.drawLine({
                        start: { x: deductionColX + deductionColWidths[index], y: currentY },
                        end: { x: deductionColX + deductionColWidths[index], y: currentY - 25 },
                        thickness: 1,
                        color: rgb(0, 0, 0),
                      })
                    }

                    deductionColX += deductionColWidths[index]
                  })

                  currentY -= 25
                }

                // Draw deduction row
                currentPage.drawRectangle({
                  x: margin,
                  y: currentY - rowHeight,
                  width: deductionColWidths.reduce((sum, width) => sum + width, 0),
                  height: rowHeight,
                  borderColor: rgb(0.92, 0.92, 0.92),
                  borderWidth: 1,
                  color: rgb(1, 1, 1),
                })

                // Draw column separator
                currentPage.drawLine({
                  start: { x: margin + deductionColWidths[0], y: currentY },
                  end: { x: margin + deductionColWidths[0], y: currentY - rowHeight },
                  thickness: 1,
                  color: rgb(0.92, 0.92, 0.92),
                })

                // Draw title (bold)
                currentPage.drawText(title, {
                  x: margin + 5,
                  y: currentY - 12,
                  size: 10,
                  font: helveticaBoldFont,
                  color: rgb(0, 0, 0),
                })

                // Draw detail if exists
                if (detailLines.length > 0) {
                  detailLines.forEach((line: string, lineIndex: number) => {
                    currentPage.drawText(line, {
                      x: margin + 5,
                      y: currentY - 24 - lineIndex * 12,
                      size: 8,
                      font: helveticaFont,
                      color: rgb(0.4, 0.4, 0.4),
                    })
                  })
                }

                // Draw score (bold, centered)
                const scoreX = margin + deductionColWidths[0]
                const scoreTextWidth = helveticaBoldFont.widthOfTextAtSize(String(score), 10)
                currentPage.drawText(String(score), {
                  x: scoreX + (deductionColWidths[1] - scoreTextWidth) / 2,
                  y: currentY - rowHeight / 2 - 2,
                  size: 10,
                  font: helveticaBoldFont,
                  color: rgb(0, 0, 0),
                })

                currentY -= rowHeight
              }
            }
          } else {
            // Criteria-based sections (like Artistic Impression, Collective Marks)
            const collectiveColWidths = [340, 60, 60, 80] // Mark, Coeffic., Score, Final
            const collectiveHeaders = ['Mark', 'Coeffic.', 'Score', 'Final']

            // Headers with dark background
            currentPage.drawRectangle({
              x: margin,
              y: currentY - 25,
              width: collectiveColWidths.reduce((sum, width) => sum + width, 0),
              height: 25,
              color: rgb(0.2, 0.2, 0.2),
              borderColor: rgb(0.92, 0.92, 0.92),
              borderWidth: 1,
            })

            let collectiveColX = margin
            collectiveHeaders.forEach((header, index) => {
              currentPage.drawText(header, {
                x: collectiveColX + 10,
                y: currentY - 18,
                size: 10,
                font: helveticaBoldFont,
                color: rgb(1, 1, 1),
              })

              if (index < collectiveHeaders.length - 1) {
                currentPage.drawLine({
                  start: { x: collectiveColX + collectiveColWidths[index], y: currentY },
                  end: { x: collectiveColX + collectiveColWidths[index], y: currentY - 25 },
                  thickness: 1,
                  color: rgb(0.92, 0.92, 0.92),
                })
              }

              collectiveColX += collectiveColWidths[index]
            })

            currentY -= 25

            // Process criteria-based rows (skip deductions/errors for now)
            for (const section of sections) {
              for (const row of section.rows) {
                // Skip deduction/error rows - we'll handle them separately
                const description = (row.description || '').toLowerCase()
                const isDeductionRow =
                  description.includes('deduction') ||
                  description.includes('error') ||
                  description.includes('forbidden')

                if (isDeductionRow) {
                  continue // Skip for now, process later
                }
                // Check if we need a new page
                if (currentY < 150) {
                  // Create new page for continuation
                  currentPage = pdfDoc.addPage([pageWidth, pageHeight])
                  currentY = pageHeight - margin

                  // Add judge header on new page
                  currentPage.drawText(`Judge ${judgeIndex + 1}, ${judgeName} (continued)`, {
                    x: margin,
                    y: currentY,
                    size: 16,
                    font: helveticaBoldFont,
                    color: rgb(0, 0, 0),
                  })

                  currentY -= 40

                  // Add section header on new page
                  currentPage.drawText(
                    `${sectionName.toUpperCase()} • Max ${sectionMax} (continued)`,
                    {
                      x: margin,
                      y: currentY,
                      size: 12,
                      font: helveticaBoldFont,
                      color: rgb(0, 0, 0),
                    }
                  )

                  currentY -= 30

                  // Re-draw headers on new page
                  currentPage.drawRectangle({
                    x: margin,
                    y: currentY - 25,
                    width: collectiveColWidths.reduce((sum, width) => sum + width, 0),
                    height: 25,
                    color: rgb(0.2, 0.2, 0.2),
                    borderColor: rgb(0.92, 0.92, 0.92),
                    borderWidth: 1,
                  })

                  let newPageColX = margin
                  collectiveHeaders.forEach((header, index) => {
                    currentPage.drawText(header, {
                      x: newPageColX + 10,
                      y: currentY - 18,
                      size: 10,
                      font: helveticaBoldFont,
                      color: rgb(1, 1, 1),
                    })

                    if (index < collectiveHeaders.length - 1) {
                      currentPage.drawLine({
                        start: { x: newPageColX + collectiveColWidths[index], y: currentY },
                        end: { x: newPageColX + collectiveColWidths[index], y: currentY - 25 },
                        thickness: 1,
                        color: rgb(0.92, 0.92, 0.92),
                      })
                    }

                    newPageColX += collectiveColWidths[index]
                  })

                  currentY -= 25
                }

                // Parse criteria description (existing collective marks logic)
                const parseCollectiveDescription = (
                  description: string
                ): { title: string; detail: string } => {
                  if (!description) return { title: '', detail: '' }

                  let title = ''
                  let detail = ''

                  const h5Match = description.match(/<h5[^>]*>(.*?)<\/h5>/i)
                  const pMatch = description.match(/<p[^>]*>(.*?)<\/p>/i)

                  if (h5Match && pMatch) {
                    title = h5Match[1].replace(/<[^>]*>/g, '').trim()
                    detail = pMatch[1].replace(/<[^>]*>/g, '').trim()
                  } else {
                    const cleanText = description
                      .replace(/<[^>]*>/g, '')
                      .replace(/&nbsp;/g, ' ')
                      .replace(/&amp;/g, '&')
                      .trim()

                    if (cleanText.includes('\n')) {
                      const lines = cleanText
                        .split('\n')
                        .map((l) => l.trim())
                        .filter((l) => l)
                      title = lines[0] || ''
                      detail = lines.slice(1).join(' ') || ''
                    } else {
                      const titleMatch = cleanText.match(/^([A-Z][a-z]*(?:\s+[A-Z][a-z]*)*)\s*(.*)/)
                      if (titleMatch) {
                        title = titleMatch[1].trim()
                        detail = titleMatch[2].trim()
                      } else {
                        const words = cleanText.split(' ')
                        title = words[0] || cleanText
                        detail = words.slice(1).join(' ') || ''
                      }
                    }
                  }

                  return { title, detail }
                }

                const { title, detail } = parseCollectiveDescription(row.description || '')
                const coefficient = row.coefficient || ''
                const score = row.score || ''
                const finalScore =
                  row.coefficient && row.score
                    ? (Number(row.coefficient) * Number(row.score)).toString()
                    : score

                const detailLines = detail
                  ? wrapText(detail, collectiveColWidths[0] - 20, 8, helveticaFont)
                  : []
                const rowHeight = Math.max(30, 18 + detailLines.length * 12)

                // Draw criteria row
                currentPage.drawRectangle({
                  x: margin,
                  y: currentY - rowHeight,
                  width: collectiveColWidths.reduce((sum, width) => sum + width, 0),
                  height: rowHeight,
                  borderColor: rgb(0.92, 0.92, 0.92),
                  borderWidth: 1,
                  color: rgb(1, 1, 1),
                })

                // Draw column separators
                collectiveColX = margin
                for (let colIndex = 0; colIndex < collectiveColWidths.length - 1; colIndex++) {
                  collectiveColX += collectiveColWidths[colIndex]
                  currentPage.drawLine({
                    start: { x: collectiveColX, y: currentY },
                    end: { x: collectiveColX, y: currentY - rowHeight },
                    thickness: 1,
                    color: rgb(0.92, 0.92, 0.92),
                  })
                }

                // Draw content (existing logic for collective marks)
                currentPage.drawText(title, {
                  x: margin + 5,
                  y: currentY - 12,
                  size: 10,
                  font: helveticaBoldFont,
                  color: rgb(0, 0, 0),
                })

                if (detailLines.length > 0) {
                  detailLines.forEach((line: string, lineIndex: number) => {
                    currentPage.drawText(line, {
                      x: margin + 5,
                      y: currentY - 24 - lineIndex * 12,
                      size: 8,
                      font: helveticaFont,
                      color: rgb(0.4, 0.4, 0.4),
                    })
                  })
                }

                // Draw numeric values (existing logic)
                const coefficientX = margin + collectiveColWidths[0]
                if (coefficient) {
                  const coefficientTextWidth = helveticaFont.widthOfTextAtSize(
                    String(coefficient),
                    10
                  )
                  currentPage.drawText(String(coefficient), {
                    x: coefficientX + (collectiveColWidths[1] - coefficientTextWidth) / 2,
                    y: currentY - rowHeight / 2 - 2,
                    size: 10,
                    font: helveticaFont,
                    color: rgb(0, 0, 0),
                  })
                }

                const scoreX = margin + collectiveColWidths[0] + collectiveColWidths[1]
                const scoreTextWidth = helveticaFont.widthOfTextAtSize(String(score), 10)
                currentPage.drawText(String(score), {
                  x: scoreX + (collectiveColWidths[2] - scoreTextWidth) / 2,
                  y: currentY - rowHeight / 2 - 2,
                  size: 10,
                  font: helveticaFont,
                  color: rgb(0, 0, 0),
                })

                const finalX =
                  margin + collectiveColWidths[0] + collectiveColWidths[1] + collectiveColWidths[2]
                const finalTextWidth = helveticaBoldFont.widthOfTextAtSize(String(finalScore), 10)
                currentPage.drawText(String(finalScore), {
                  x: finalX + (collectiveColWidths[3] - finalTextWidth) / 2,
                  y: currentY - rowHeight / 2 - 2,
                  size: 10,
                  font: helveticaBoldFont,
                  color: rgb(0, 0, 0),
                })

                currentY -= rowHeight

                // Handle remarks for criteria rows
                const rowRemarks = row.remarks || row.remark || row.comment || ''
                if (rowRemarks && rowRemarks.trim() && currentY > 100) {
                  const remarksLabelWidth = helveticaBoldFont.widthOfTextAtSize('Remarks:', 8)
                  const spaceWidth = helveticaBoldFont.widthOfTextAtSize(' ', 8)
                  const remarksTextWidth =
                    collectiveColWidths[0] - remarksLabelWidth - spaceWidth - 10
                  const remarksLines = wrapText(rowRemarks, remarksTextWidth, 8, helveticaFont)
                  const remarksHeight = Math.max(20, remarksLines.length * 12 + 8)

                  currentPage.drawRectangle({
                    x: margin,
                    y: currentY - remarksHeight,
                    width: collectiveColWidths.reduce((sum, width) => sum + width, 0),
                    height: remarksHeight,
                    borderColor: rgb(0.92, 0.92, 0.92),
                    borderWidth: 1,
                    color: rgb(1, 1, 1),
                  })

                  collectiveColX = margin
                  for (let colIndex = 0; colIndex < collectiveColWidths.length - 1; colIndex++) {
                    collectiveColX += collectiveColWidths[colIndex]
                    currentPage.drawLine({
                      start: { x: collectiveColX, y: currentY },
                      end: { x: collectiveColX, y: currentY - remarksHeight },
                      thickness: 1,
                      color: rgb(0.92, 0.92, 0.92),
                    })
                  }

                  currentPage.drawText('Remarks:', {
                    x: margin + 5,
                    y: currentY - 12,
                    size: 8,
                    font: helveticaBoldFont,
                    color: rgb(0.3, 0.3, 0.3),
                  })

                  remarksLines.forEach((line: string, lineIndex: number) => {
                    currentPage.drawText(line, {
                      x: margin + 5 + remarksLabelWidth + spaceWidth,
                      y: currentY - 12 - lineIndex * 12,
                      size: 8,
                      font: helveticaFont,
                      color: rgb(0.4, 0.4, 0.4),
                    })
                  })

                  currentY -= remarksHeight
                }
              }
            }

            // Now process deduction/error rows for criteria-based sections
            const criteriaDeductionRows: any[] = []
            for (const section of sections) {
              for (const row of section.rows) {
                const description = (row.description || '').toLowerCase()
                const isDeductionRow =
                  description.includes('deduction') ||
                  description.includes('error') ||
                  description.includes('forbidden')
                if (isDeductionRow) {
                  criteriaDeductionRows.push(row)
                }
              }
            }

            if (criteriaDeductionRows.length > 0) {
              // Add space before deductions section
              currentY -= 30

              // Check if we need a new page for deductions
              if (currentY < 200) {
                currentPage = pdfDoc.addPage([pageWidth, pageHeight])
                currentY = pageHeight - margin

                // Add judge header on new page
                currentPage.drawText(`Judge ${judgeIndex + 1}, ${judgeName} (continued)`, {
                  x: margin,
                  y: currentY,
                  size: 16,
                  font: helveticaBoldFont,
                  color: rgb(0, 0, 0),
                })
                currentY -= 40
              }

              // Deductions section header
              currentPage.drawText('DEDUCTIONS & ERRORS', {
                x: margin,
                y: currentY,
                size: 12,
                font: helveticaBoldFont,
                color: rgb(0, 0, 0),
              })

              currentY -= 30

              // Simple two-column layout for deductions: Description | Score
              const criteriaDeductionColWidths = [460, 80] // Description, Score
              const criteriaDeductionHeaders = ['Description', 'Score']

              // Deduction headers
              currentPage.drawRectangle({
                x: margin,
                y: currentY - 25,
                width: criteriaDeductionColWidths.reduce((sum, width) => sum + width, 0),
                height: 25,
                color: rgb(0.2, 0.2, 0.2),
                borderColor: rgb(0.92, 0.92, 0.92),
                borderWidth: 1,
              })

              let criteriaDeductionColX = margin
              criteriaDeductionHeaders.forEach((header, index) => {
                currentPage.drawText(header, {
                  x: criteriaDeductionColX + 10,
                  y: currentY - 18,
                  size: 10,
                  font: helveticaBoldFont,
                  color: rgb(1, 1, 1),
                })

                if (index < criteriaDeductionHeaders.length - 1) {
                  currentPage.drawLine({
                    start: {
                      x: criteriaDeductionColX + criteriaDeductionColWidths[index],
                      y: currentY,
                    },
                    end: {
                      x: criteriaDeductionColX + criteriaDeductionColWidths[index],
                      y: currentY - 25,
                    },
                    thickness: 1,
                    color: rgb(0.92, 0.92, 0.92),
                  })
                }

                criteriaDeductionColX += criteriaDeductionColWidths[index]
              })

              currentY -= 25

              // Process each deduction row
              for (const row of criteriaDeductionRows) {
                const parseDeductionDescription = (
                  description: string
                ): { title: string; detail: string } => {
                  if (!description) return { title: '', detail: '' }

                  // Clean HTML
                  const cleanText = description
                    .replace(/<[^>]*>/g, '')
                    .replace(/&nbsp;/g, ' ')
                    .replace(/&amp;/g, '&')
                    .trim()

                  // Look for h5 tags (title) and p tags (detail)
                  const h5Match = description.match(/<h5[^>]*>(.*?)<\/h5>/i)
                  const pMatch = description.match(/<p[^>]*>(.*?)<\/p>/i)

                  if (h5Match && pMatch) {
                    return {
                      title: h5Match[1].replace(/<[^>]*>/g, '').trim(),
                      detail: pMatch[1].replace(/<[^>]*>/g, '').trim(),
                    }
                  }

                  // Fallback to clean text
                  return { title: cleanText, detail: '' }
                }

                const { title, detail } = parseDeductionDescription(row.description || '')
                const score = row.score || '0'

                // Calculate row height based on content
                const detailLines = detail
                  ? wrapText(detail, criteriaDeductionColWidths[0] - 20, 8, helveticaFont)
                  : []
                const rowHeight = Math.max(30, 18 + detailLines.length * 12)

                // Check if we need a new page
                if (currentY < rowHeight + 50) {
                  currentPage = pdfDoc.addPage([pageWidth, pageHeight])
                  currentY = pageHeight - margin

                  // Add continuation headers
                  currentPage.drawText(
                    `Judge ${judgeIndex + 1}, ${judgeName} - Deductions & Errors (continued)`,
                    {
                      x: margin,
                      y: currentY,
                      size: 16,
                      font: helveticaBoldFont,
                      color: rgb(0, 0, 0),
                    }
                  )
                  currentY -= 40

                  // Re-draw headers
                  currentPage.drawRectangle({
                    x: margin,
                    y: currentY - 25,
                    width: criteriaDeductionColWidths.reduce((sum, width) => sum + width, 0),
                    height: 25,
                    color: rgb(0.2, 0.2, 0.2),
                    borderColor: rgb(0.92, 0.92, 0.92),
                    borderWidth: 1,
                  })

                  criteriaDeductionColX = margin
                  criteriaDeductionHeaders.forEach((header, index) => {
                    currentPage.drawText(header, {
                      x: criteriaDeductionColX + 10,
                      y: currentY - 18,
                      size: 10,
                      font: helveticaBoldFont,
                      color: rgb(1, 1, 1),
                    })

                    if (index < criteriaDeductionHeaders.length - 1) {
                      currentPage.drawLine({
                        start: {
                          x: criteriaDeductionColX + criteriaDeductionColWidths[index],
                          y: currentY,
                        },
                        end: {
                          x: criteriaDeductionColX + criteriaDeductionColWidths[index],
                          y: currentY - 25,
                        },
                        thickness: 1,
                        color: rgb(0.92, 0.92, 0.92),
                      })
                    }

                    criteriaDeductionColX += criteriaDeductionColWidths[index]
                  })

                  currentY -= 25
                }

                // Draw deduction row
                currentPage.drawRectangle({
                  x: margin,
                  y: currentY - rowHeight,
                  width: criteriaDeductionColWidths.reduce((sum, width) => sum + width, 0),
                  height: rowHeight,
                  borderColor: rgb(0.92, 0.92, 0.92),
                  borderWidth: 1,
                  color: rgb(1, 1, 1),
                })

                // Draw column separator
                currentPage.drawLine({
                  start: { x: margin + criteriaDeductionColWidths[0], y: currentY },
                  end: { x: margin + criteriaDeductionColWidths[0], y: currentY - rowHeight },
                  thickness: 1,
                  color: rgb(0.92, 0.92, 0.92),
                })

                // Draw title (bold)
                currentPage.drawText(title, {
                  x: margin + 5,
                  y: currentY - 12,
                  size: 10,
                  font: helveticaBoldFont,
                  color: rgb(0, 0, 0),
                })

                // Draw detail if exists
                if (detailLines.length > 0) {
                  detailLines.forEach((line: string, lineIndex: number) => {
                    currentPage.drawText(line, {
                      x: margin + 5,
                      y: currentY - 24 - lineIndex * 12,
                      size: 8,
                      font: helveticaFont,
                      color: rgb(0.4, 0.4, 0.4),
                    })
                  })
                }

                // Draw score (bold, centered)
                const scoreX = margin + criteriaDeductionColWidths[0]
                const scoreTextWidth = helveticaBoldFont.widthOfTextAtSize(String(score), 10)
                currentPage.drawText(String(score), {
                  x: scoreX + (criteriaDeductionColWidths[1] - scoreTextWidth) / 2,
                  y: currentY - rowHeight / 2 - 2,
                  size: 10,
                  font: helveticaBoldFont,
                  color: rgb(0, 0, 0),
                })

                currentY -= rowHeight
              }
            }
          }

          // Add space between sections if not the last section
          const sectionKeys = Object.keys(sectionGroups)
          const currentSectionIndex = sectionKeys.indexOf(sectionName)
          if (currentSectionIndex < sectionKeys.length - 1) {
            currentY -= 30
          }
        }
      }

      // Summary section - check if we have space on current page or need new page
      const summaryRequiredHeight = 300 // Estimate height needed for summary + signature

      // Use the current page and position from section processing
      let summaryPage = currentPage
      let summaryPageY = currentY
      let needsHeader = false

      if (currentY < summaryRequiredHeight) {
        // Not enough space, create a new page
        summaryPage = pdfDoc.addPage([pageWidth, pageHeight])
        summaryPageY = pageHeight - margin
        needsHeader = true
      }

      // Add judge header only if we're on a new page
      if (needsHeader) {
        summaryPage.drawText(`Judge ${judgeIndex + 1}, ${judgeName} - Summary`, {
          x: margin,
          y: summaryPageY,
          size: 16,
          font: helveticaBoldFont,
          color: rgb(0, 0, 0),
        })
        summaryPageY -= 40
      }

      summaryPageY -= 30

      summaryPage.drawText(`SUMMARY • Max ${judgeMax}`, {
        x: margin,
        y: summaryPageY,
        size: 12,
        font: helveticaBoldFont,
        color: rgb(0, 0, 0),
      })

      summaryPageY -= 30

      // Summary table with same full width as individual tasks table
      const summaryColWidths = [400, 140] // Test, Total columns (same total width as individual tasks)
      const summaryHeaders = ['Test', 'Total']

      // Summary table headers with dark background
      summaryPage.drawRectangle({
        x: margin,
        y: summaryPageY - 25,
        width: summaryColWidths.reduce((sum, width) => sum + width, 0),
        height: 25,
        color: rgb(0.2, 0.2, 0.2), // Dark background like movement headers
        borderColor: rgb(0.92, 0.92, 0.92),
        borderWidth: 1,
      })

      let summaryColX = margin
      summaryHeaders.forEach((header, index) => {
        summaryPage.drawText(header, {
          x: summaryColX + 10,
          y: summaryPageY - 18,
          size: 10,
          font: helveticaBoldFont,
          color: rgb(1, 1, 1), // White text on dark background
        })

        if (index < summaryHeaders.length - 1) {
          summaryPage.drawLine({
            start: { x: summaryColX + summaryColWidths[index], y: summaryPageY },
            end: { x: summaryColX + summaryColWidths[index], y: summaryPageY - 25 },
            thickness: 1,
            color: rgb(0.92, 0.92, 0.92),
          })
        }

        summaryColX += summaryColWidths[index]
      })

      summaryPageY -= 25

      // Calculate totals for each section group dynamically
      const sectionTotals: { [key: string]: { total: number; max: number } } = {}
      let finalJudgeTotal = 0

      if (judge.sections) {
        // Group sections by their name for summary calculations
        const sectionGroups: { [key: string]: any[] } = {}
        judge.sections.forEach((section) => {
          const sectionName = section.name || 'Unknown Section'
          if (!sectionGroups[sectionName]) {
            sectionGroups[sectionName] = []
          }
          sectionGroups[sectionName].push(section)
        })

        // Calculate totals for each section group
        Object.entries(sectionGroups).forEach(([sectionName, sections]) => {
          let sectionTotal = 0
          let sectionMax = 0

          sections.forEach((section: any) => {
            section.rows.forEach((row: any) => {
              if (row.coefficient) {
                sectionTotal += Number(row.coefficient) * Number(row.score || 0)
              } else if (row.fraction === 'minus') {
                sectionTotal -= Number(row.score || 0)
              } else {
                sectionTotal += Number(row.score || 0)
              }
            })
            sectionMax += section.max || 0
          })

          sectionTotals[sectionName] = { total: sectionTotal, max: sectionMax }
          finalJudgeTotal += sectionTotal
        })

        // Subtract errors
        finalJudgeTotal -= judge.errors || 0
      }

      // Build summary data rows dynamically based on actual sections
      const summaryData: string[][] = []

      // Add each section total
      Object.entries(sectionTotals).forEach(([sectionName, totals]) => {
        summaryData.push([`${sectionName} Total`, `${totals.total}`, `Max Score: ${totals.max}`])
      })

      // Add errors and final total
      summaryData.push(['Errors', `${judge.errors || 0}`])
      summaryData.push(['Total Score', `${finalJudgeTotal} • ${judgePercentage}%`])

      summaryData.forEach((item) => {
        // Increase row height if there's a sub-text (max score)
        const hasSubText = item.length > 2 && item[2]
        const rowHeight = hasSubText ? 35 : 25

        // Draw summary row with light background
        summaryPage.drawRectangle({
          x: margin,
          y: summaryPageY - rowHeight,
          width: summaryColWidths.reduce((sum, width) => sum + width, 0),
          height: rowHeight,
          borderColor: rgb(0.92, 0.92, 0.92),
          borderWidth: 1,
          color: rgb(1, 1, 1), // White background
        })

        // Draw column separator
        summaryPage.drawLine({
          start: { x: margin + summaryColWidths[0], y: summaryPageY },
          end: { x: margin + summaryColWidths[0], y: summaryPageY - rowHeight },
          thickness: 1,
          color: rgb(0.92, 0.92, 0.92),
        })

        // Draw test name (left column) - bold
        summaryPage.drawText(item[0], {
          x: margin + 10,
          y: hasSubText ? summaryPageY - 12 : summaryPageY - rowHeight / 2 - 2,
          size: 10,
          font: helveticaBoldFont,
          color: rgb(0, 0, 0),
        })

        // Draw total value (right column) - bold, centered horizontally and vertically
        const totalTextWidth = helveticaBoldFont.widthOfTextAtSize(item[1], 10)
        summaryPage.drawText(item[1], {
          x: margin + summaryColWidths[0] + (summaryColWidths[1] - totalTextWidth) / 2,
          y: summaryPageY - rowHeight / 2 - 5,
          size: 10,
          font: helveticaBoldFont,
          color: rgb(0, 0, 0),
        })

        // Draw sub-text if exists (max score) - not bold, smaller, under the main title
        if (hasSubText) {
          summaryPage.drawText(item[2], {
            x: margin + 10,
            y: summaryPageY - 24,
            size: 8,
            font: helveticaFont, // Regular font, not bold
            color: rgb(0.4, 0.4, 0.4), // Lighter color
          })
        }

        summaryPageY -= rowHeight
      })

      // Further Remarks section if exists
      const judgeRemarks = judge.furtherRemarks || judge.furtherRemarks || ''
      if (judgeRemarks && judgeRemarks.trim()) {
        summaryPageY -= 30

        summaryPage.drawText('FURTHER REMARKS', {
          x: margin,
          y: summaryPageY,
          size: 12,
          font: helveticaBoldFont,
          color: rgb(0, 0, 0),
        })

        summaryPageY -= 20

        // Calculate height needed for further remarks (allow for text wrapping)
        const furtherRemarksWidth = contentWidth - 20 // Account for padding
        const furtherRemarksLines = wrapText(judgeRemarks, furtherRemarksWidth, 10, helveticaFont)
        const furtherRemarksHeight = Math.max(40, furtherRemarksLines.length * 14 + 20)

        // Draw further remarks box
        summaryPage.drawRectangle({
          x: margin,
          y: summaryPageY - furtherRemarksHeight,
          width: contentWidth,
          height: furtherRemarksHeight,
          borderColor: rgb(0.92, 0.92, 0.92),
          borderWidth: 1,
          color: rgb(0.98, 0.98, 0.98), // Very light background
        })

        // Draw each line of further remarks
        furtherRemarksLines.forEach((line: string, lineIndex: number) => {
          summaryPage.drawText(line, {
            x: margin + 10,
            y: summaryPageY - 15 - lineIndex * 14,
            size: 10,
            font: helveticaFont,
            color: rgb(0.2, 0.2, 0.2),
          })
        })

        summaryPageY -= furtherRemarksHeight
      }

      // Add judges signature section - always include it
      summaryPageY -= 30

      summaryPage.drawText('JUDGES SIGNATURE', {
        x: margin,
        y: summaryPageY,
        size: 12,
        font: helveticaBoldFont,
        color: rgb(0, 0, 0),
      })

      summaryPageY -= 30

      // Judge info line
      summaryPage.drawText(`Judge ${judgeIndex + 1}, ${judgeName}`, {
        x: margin,
        y: summaryPageY,
        size: 10,
        font: helveticaFont,
        color: rgb(0, 0, 0),
      })

      // Date signed and method
      const currentDate = new Date()
      const formattedDate = `${String(currentDate.getDate()).padStart(2, '0')}/${String(currentDate.getMonth() + 1).padStart(2, '0')}/${String(currentDate.getFullYear()).slice(-2)}`
      const timeString = currentDate
        .toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        })
        .toLowerCase()

      summaryPageY -= 20
      summaryPage.drawText(`Date signed: ${timeString}, ${formattedDate}`, {
        x: margin,
        y: summaryPageY,
        size: 9,
        font: helveticaFont,
        color: rgb(0, 0, 0),
      })

      summaryPageY -= 15
      summaryPage.drawText('Method: Electronic signature, Pegasus Event Management System', {
        x: margin,
        y: summaryPageY,
        size: 9,
        font: helveticaFont,
        color: rgb(0, 0, 0),
      })

      // Signature box and image
      summaryPageY -= 30
      summaryPage.drawRectangle({
        x: margin,
        y: summaryPageY - 60,
        width: 300,
        height: 60,
        borderColor: rgb(0.92, 0.92, 0.92),
        borderWidth: 1,
        color: rgb(1, 1, 1),
      })

      // Check if judge has a signature and display it
      const judgeSignatureUrl = judge.signatureUrl || judge.signatureUrl
      if (judgeSignatureUrl) {
        try {
          // Fetch the signature image
          const signatureResponse = await fetch(judgeSignatureUrl)
          const signatureBytes = await signatureResponse.arrayBuffer()

          // Embed the signature image (assuming PNG format)
          const signatureImage = await pdfDoc.embedPng(signatureBytes)

          // Get image dimensions and scale to fit within signature box
          const signatureDims = signatureImage.scale(1)
          const boxWidth = 280 // Slightly smaller than rectangle width
          const boxHeight = 40 // Slightly smaller than rectangle height

          // Calculate scaling to fit within box while maintaining aspect ratio
          const scaleX = boxWidth / signatureDims.width
          const scaleY = boxHeight / signatureDims.height
          const scale = Math.min(scaleX, scaleY, 1) // Don't scale up, only down

          const scaledWidth = signatureDims.width * scale
          const scaledHeight = signatureDims.height * scale

          // Center the signature within the box
          const signatureX = margin + 10 + (boxWidth - scaledWidth) / 2
          const signatureY = summaryPageY - 60 + (60 - scaledHeight) / 2

          summaryPage.drawImage(signatureImage, {
            x: signatureX,
            y: signatureY,
            width: scaledWidth,
            height: scaledHeight,
          })
        } catch (error) {
          console.error('Failed to load judge signature:', error)
          // Fallback to placeholder text if image loading fails
          summaryPage.drawText('[Judges electronic signature - failed to load]', {
            x: margin + 10,
            y: summaryPageY - 35,
            size: 9,
            font: helveticaFont,
            color: rgb(0.5, 0.5, 0.5),
          })
        }
      } else {
        // No signature available, show placeholder
        summaryPage.drawText('[Judges electronic signature]', {
          x: margin + 10,
          y: summaryPageY - 35,
          size: 9,
          font: helveticaFont,
          color: rgb(0.5, 0.5, 0.5),
        })
      }
    }
  }

  const fileName = `Scoring_Results_${riderName.replace(/\s+/g, '_')}_${scoreCard.replace(/\s+/g, '_')}.pdf`
  await savePdf(pdfDoc, fileName)
}
